import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const EventDateSelectionStep = ({
  event,
  inquiryForm,
  inquiryError,
  isCustomInquiry,
  setIsCustomInquiry,
  handleInquiryChange,
  handleEventDatesChange,
  calculateTripDetails,
  formRef
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const { t } = useTranslation();

  return (
    <>
      <div className="space-y-4 border-x border-b border-gray-300 p-4 rounded-b-md pt-10 pb-10">
        {inquiryError && (
          <p className="text-red-500 text-center">{inquiryError}</p>
        )}

        <div className="flex flex-col">
          <label className="text-black block text-lg font-semibold mb-2">
            {t("EventPage.pleaseSelectPreferredDate")}
          </label>

          {!isCustomInquiry && (
            <>
              <div className="border-b-2 border-gray-900 p-2 hidden lg:flex lg:flex-row gap-2 lg:gap-4 font-semibold text-black">
                <div className="flex-1">{t("EventPage.eventDate")}</div>
                <div className="flex-1">{t("EventPage.tripDates")}</div>
                <div className="flex-1">{t("EventPage.weekdays")}</div>
                <div className="flex-1">{t("EventPage.additionalFee")}</div>
              </div>
              <div className="space-y-0">
                {event.date_times.map((date, index) => {
                  const { tripDates, weekdays } = calculateTripDetails(
                    date?.date
                  );
                  const isExpanded = expandedIndex === index;
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        !isCustomInquiry && handleEventDatesChange(date);
                        if (formRef.current) {
                          formRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                          setTimeout(() => {
                            window.scrollBy({
                              top: -120,
                              left: 0,
                              behavior: "smooth",
                            });
                          }, 300);
                        }
                      }
                      }
                      className={`text-sm px-2 py-4 flex flex-col lg:flex-row gap-2 lg:gap-4 cursor-pointer transition-colors border-b-2 border-gray-900
                      ${inquiryForm.eventDates[0] === date?.date
                          ? "bg-[#f5e5e4] text-black"
                          : "bg-white text-black hover:bg-gray-100"
                        }
                      ${isCustomInquiry ? "cursor-not-allowed opacity-50" : ""}
                    `}
                    >
                      <div className="flex justify-between items-start gap-1 lg:flex-1">
                        <div className="flex gap-1">
                          {isExpanded && (
                            <p className="font-semibold lg:hidden">
                              {t("EventPage.eventDate")}
                            </p>
                          )}
                          {(() => {
                            const dateObj = new Date(date.date);

                            if (isNaN(dateObj.getTime())) return ""; // invalid date

                            // Check if input has a time component
                            const hasTime = date.date.includes("T");

                            return dateObj.toLocaleString("en-UK", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              ...(hasTime && {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              }),
                            });
                          })()}

                        </div>
                        <div className="flex items-center lg:hidden">
                          <button
                            type="button"
                            className="mr-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedIndex(isExpanded ? null : index);
                            }}
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <FontAwesomeIcon icon={faAngleUp} />
                            ) : (
                              <FontAwesomeIcon icon={faAngleDown} />
                            )}
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="lg:hidden -mt-2">
                          <div className="flex gap-2">
                            <p className="font-semibold">{t("EventPage.tripDates")}</p>
                            {tripDates}
                          </div>
                          <div className="flex gap-2">
                            <p className="font-semibold">{t("EventPage.weekdays")}</p>
                            {weekdays}
                          </div>
                          <div className="flex gap-2">
                            <p className="font-semibold">{t("EventPage.additionalFee")}</p>
                            {date?.cost != null && date.cost !== "0" && (
                              <>
                                {date.cost < 0
                                  ? `-€${Math.abs(date.cost).toLocaleString(
                                    "en-US",
                                    {
                                      maximumFractionDigits: 0,
                                    }
                                  )}`
                                  : `+€${date.cost.toLocaleString("en-US", {
                                    maximumFractionDigits: 0,
                                  })}`}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex-col gap-1 lg:flex-1 hidden lg:block">
                        <p className="font-semibold lg:hidden">{t("EventPage.tripDates")}</p>
                        {tripDates}
                      </div>
                      <div className="flex-col gap-1 lg:flex-1 hidden lg:block">
                        <p className="font-semibold lg:hidden">{t("EventPage.weekdays")}</p>
                        {weekdays}
                      </div>
                      <div className="flex-col gap-1 lg:flex-1 hidden lg:block">
                        <p className="font-semibold lg:hidden">
                          {t("EventPage.additionalFee")}
                        </p>
                        {date?.cost != null && date.cost !== "0" && (
                          <>
                            {date.cost < 0
                              ? `-€${Math.abs(date.cost).toLocaleString(
                                "en-US",
                                {
                                  maximumFractionDigits: 0,
                                }
                              )}`
                              : `+€${date.cost.toLocaleString("en-US", {
                                maximumFractionDigits: 0,
                              })}`}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isCustomInquiry}
            onChange={(e) => {
              setIsCustomInquiry(e.target.checked);
              handleEventDatesChange();
              if (formRef.current) {
                formRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
                setTimeout(() => {
                  window.scrollBy({
                    top: -120,
                    left: 0,
                    behavior: "smooth",
                  });
                }, 300);
              }
            }}
            className="h-4 w-4 text-opera-red"
          />
          <label className="text-black">
            {t("EventPage.individualTravelRequest")}
          </label>
        </div>

        {isCustomInquiry && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1 ">
              <label className="text-black">{t("EventPage.customStartDate")} *</label>
              <input
                type="date"
                name="customStartDate"
                value={inquiryForm.customStartDate}
                onChange={handleInquiryChange}
                min={new Date().toISOString().split("T")[0]}
                className="w-full p-2 text-base border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-opera-red focus:border-opera-red"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-black">{t("EventPage.customEndDate")} *</label>
              <input
                type="date"
                name="customEndDate"
                value={inquiryForm.customEndDate}
                onChange={handleInquiryChange}
                min={
                  inquiryForm.customStartDate ||
                  new Date().toISOString().split("T")[0]
                }
                className="w-full p-2 text-base text-black border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-opera-red focus:border-opera-red placeholder:text-black"
              />
            </div>
            {inquiryForm.customStartDate &&
              inquiryForm.customEndDate &&
              new Date(inquiryForm.customEndDate) <=
              new Date(inquiryForm.customStartDate) && (
                <p className="text-red-500 text-sm">
                  {t("EventPage.endDateAfterStartDate")}
                </p>
              )}
          </div>
        )}
      </div>
    </>
  );
};
